import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Partner, PartnerAnsprechpartner, Preiskondition, ERechnungFormat } from '../types';
import CustomFieldsForm from './CustomFieldsForm';
import AktivitaetenBlock from './AktivitaetenBlock';
import { UnternehmerAuswahl, unternehmerAuswahl, unternehmerPayload } from '../lib/unternehmer';
import './PartnerForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface PartnerFormProps {
  editingPartner?: Partner | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const PartnerForm: React.FC<PartnerFormProps> = ({ editingPartner, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    typ: 'Kunde' as 'Kunde' | 'Lieferant' | 'Beides',
    name: '',
    adresse: '',
    kontakt_email: '',
    kontakt_telefon: '',
    zahlungsbedingungen: '',
    iban: '',
    bic: '',
    ust_idnr: '',
    // B2B/B2C (Paket 3): neue Kunden starten „unbekannt“ (Backend-Standard: mit USt-IdNr Unternehmer)
    unternehmer: 'unbekannt' as UnternehmerAuswahl,
    // E-Rechnung (M3 / AP 3.2)
    strasse: '',
    plz: '',
    ort: '',
    land_code: 'DE',
    leitweg_id: '',
    e_rechnung_format: 'KEINE' as ERechnungFormat
  });
  // Hat der Nutzer das Kennzeichen bei einem NEUEN Kunden angefasst? Sonst entscheidet der Backend-Standard.
  const [unternehmerBeruehrt, setUnternehmerBeruehrt] = useState(false);
  const [customAttributes, setCustomAttributes] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const authToken = localStorage.getItem('auth_token') || '';

  const [ansprechpartner, setAnsprechpartner] = useState<PartnerAnsprechpartner[]>([]);
  const [neuerAnsprechpartner, setNeuerAnsprechpartner] = useState({ name: '', position: '', email: '', telefon: '', ist_hauptkontakt: false });
  const [apError, setApError] = useState('');

  const [konditionen, setKonditionen] = useState<Preiskondition[]>([]);
  const [artikelListe, setArtikelListe] = useState<{ artikel_id: number; bezeichnung: string }[]>([]);
  const [neueKondition, setNeueKondition] = useState({ artikel_id: '', staffel_ab_menge: '1', preis: '' });
  const [kError, setKError] = useState('');

  useEffect(() => {
    if (editingPartner) {
      setFormData({
        typ: editingPartner.typ,
        name: editingPartner.name,
        adresse: editingPartner.adresse || '',
        kontakt_email: editingPartner.kontakt_email || '',
        kontakt_telefon: editingPartner.kontakt_telefon || '',
        zahlungsbedingungen: editingPartner.zahlungsbedingungen || '',
        iban: editingPartner.iban || '',
        bic: editingPartner.bic || '',
        ust_idnr: editingPartner.ust_idnr || '',
        unternehmer: unternehmerAuswahl(editingPartner.unternehmer),
        strasse: editingPartner.strasse || '',
        plz: editingPartner.plz || '',
        ort: editingPartner.ort || '',
        land_code: editingPartner.land_code || 'DE',
        leitweg_id: editingPartner.leitweg_id || '',
        e_rechnung_format: editingPartner.e_rechnung_format || 'KEINE'
      });
      setCustomAttributes((editingPartner as any).custom_attributes || {});
    }
  }, [editingPartner]);

  const loadAnsprechpartner = useCallback(async () => {
    if (!editingPartner) return;
    try {
      const res = await axios.get(`${API_URL}/api/partners/${editingPartner.partner_id}/ansprechpartner`);
      setAnsprechpartner(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Ansprechpartner:', err);
    }
  }, [editingPartner]);

  useEffect(() => { loadAnsprechpartner(); }, [loadAnsprechpartner]);

  const handleAddAnsprechpartner = async () => {
    if (!editingPartner) return;
    setApError('');
    if (!neuerAnsprechpartner.name.trim()) {
      setApError('Name ist erforderlich');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/partners/${editingPartner.partner_id}/ansprechpartner`, neuerAnsprechpartner);
      setNeuerAnsprechpartner({ name: '', position: '', email: '', telefon: '', ist_hauptkontakt: false });
      loadAnsprechpartner();
    } catch (err: any) {
      setApError(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleDeleteAnsprechpartner = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/partners/ansprechpartner/${id}`);
      loadAnsprechpartner();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const loadKonditionen = useCallback(async () => {
    if (!editingPartner) return;
    try {
      const res = await axios.get(`${API_URL}/api/preiskonditionen`, { params: { partner_id: editingPartner.partner_id } });
      setKonditionen(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Preiskonditionen:', err);
    }
  }, [editingPartner]);

  useEffect(() => { loadKonditionen(); }, [loadKonditionen]);

  useEffect(() => {
    if (!editingPartner) return;
    axios.get(`${API_URL}/api/artikel`).then(res => {
      setArtikelListe(res.data.map((a: any) => ({ artikel_id: a.artikel_id, bezeichnung: a.bezeichnung })));
    }).catch(err => console.error('Fehler beim Laden der Artikel:', err));
  }, [editingPartner]);

  const handleAddKondition = async () => {
    if (!editingPartner) return;
    setKError('');
    if (!neueKondition.artikel_id) {
      setKError('Artikel ist erforderlich');
      return;
    }
    if (!neueKondition.preis) {
      setKError('Preis ist erforderlich');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/preiskonditionen`, {
        partner_id: editingPartner.partner_id,
        artikel_id: Number(neueKondition.artikel_id),
        staffel_ab_menge: Number(neueKondition.staffel_ab_menge || 1),
        preis: Number(neueKondition.preis),
      });
      setNeueKondition({ artikel_id: '', staffel_ab_menge: '1', preis: '' });
      loadKonditionen();
    } catch (err: any) {
      setKError(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleDeleteKondition = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/preiskonditionen/${id}`);
      loadKonditionen();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const artikelName = (id: number) => artikelListe.find(a => a.artikel_id === id)?.bezeichnung || `#${id}`;

  /**
   * Pflichtfelder, die bei aktivem E-Rechnungs-Format noch fehlen (M3 / AP 3.2).
   * Spiegelt die Backend-Pruefung in ``zugferd_service.validiere_stammdaten``
   * fuer die Partner-Seite — der Anwender soll den Fehler im Formular sehen und
   * nicht erst beim Rechnungsversand.
   */
  const fehlendeERechnungFelder = (): string[] => {
    if (formData.e_rechnung_format === 'KEINE') return [];
    const fehlt: string[] = [];
    if (!formData.strasse.trim()) fehlt.push('Straße');
    if (!formData.plz.trim()) fehlt.push('PLZ');
    if (!formData.ort.trim()) fehlt.push('Ort');
    if (formData.e_rechnung_format === 'XRECHNUNG' && !formData.leitweg_id.trim()) {
      fehlt.push('Leitweg-ID');
    }
    return fehlt;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setLoading(true);

    const { unternehmer: unternehmerAuswahlWert, ...formOhneUnternehmer } = formData;
    const payload: any = {
      ...formOhneUnternehmer,
      ...unternehmerPayload(unternehmerAuswahlWert, !editingPartner, unternehmerBeruehrt),
    };
    if (Object.keys(customAttributes).length > 0) {
      payload.custom_attributes = customAttributes;
    }

    try {
      if (editingPartner) {
        await axios.put(`${API_URL}/api/partners/${editingPartner.partner_id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/partners`, payload);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="partner-form-container">
      <h2>{editingPartner ? 'Partner bearbeiten' : 'Neuer Partner'}</h2>

      <form onSubmit={handleSubmit} className="partner-form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>Typ *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="Kunde"
                checked={formData.typ === 'Kunde'}
                onChange={(e) => setFormData({ ...formData, typ: e.target.value as any })}
              />
              Kunde
            </label>
            <label>
              <input
                type="radio"
                value="Lieferant"
                checked={formData.typ === 'Lieferant'}
                onChange={(e) => setFormData({ ...formData, typ: e.target.value as any })}
              />
              Lieferant
            </label>
            <label>
              <input
                type="radio"
                value="Beides"
                checked={formData.typ === 'Beides'}
                onChange={(e) => setFormData({ ...formData, typ: e.target.value as any })}
              />
              Beides
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label>Adresse</label>
          <textarea
            value={formData.adresse}
            onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>E-Mail</label>
          <input
            type="email"
            value={formData.kontakt_email}
            onChange={(e) => setFormData({ ...formData, kontakt_email: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Telefon</label>
          <input
            type="tel"
            value={formData.kontakt_telefon}
            onChange={(e) => setFormData({ ...formData, kontakt_telefon: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Zahlungsbedingungen</label>
          <input
            type="text"
            value={formData.zahlungsbedingungen}
            onChange={(e) => setFormData({ ...formData, zahlungsbedingungen: e.target.value })}
            placeholder="z.B. 30 Tage netto"
          />
        </div>

        <div className="form-group">
          <label>IBAN</label>
          <input
            type="text"
            value={formData.iban}
            onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
            placeholder="z.B. DE89 3704 0044 0532 0130 00"
          />
        </div>

        <div className="form-group">
          <label>BIC</label>
          <input
            type="text"
            value={formData.bic}
            onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
            placeholder="z.B. GENODEF1XXX"
          />
        </div>

        <div className="form-group">
          <label>USt-IdNr.</label>
          <input
            type="text"
            value={formData.ust_idnr}
            onChange={(e) => setFormData({ ...formData, ust_idnr: e.target.value })}
            placeholder="z.B. DE123456789"
          />
        </div>

        {formData.typ !== 'Lieferant' && (
          <div className="form-group">
            <label>Kundenart (B2B / B2C)</label>
            <div className="radio-group" role="radiogroup" aria-label="Kundenart">
              {([
                ['unternehmer', 'Unternehmer (B2B)'],
                ['privat', 'Privatkunde (B2C)'],
                ['unbekannt', 'Unbekannt'],
              ] as Array<[UnternehmerAuswahl, string]>).map(([wert, label]) => (
                <label key={wert}>
                  <input
                    type="radio"
                    name="unternehmer"
                    value={wert}
                    checked={formData.unternehmer === wert}
                    onChange={() => { setFormData({ ...formData, unternehmer: wert }); setUnternehmerBeruehrt(true); }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4 }}>
              Unternehmer erhalten ab dem Pflicht-Stichtag E-Rechnungen; bei Privatkunden druckt die Rechnung den
              Aufbewahrungshinweis. „Unbekannt“ löst weder das eine noch das andere aus
              {!editingPartner && ' – bei einem neuen Kunden mit USt-IdNr. wird ohne Auswahl Unternehmer angenommen'}.
            </div>
          </div>
        )}

        <fieldset className="form-group" style={{ border: '1px solid var(--bone-3, #ddd)', borderRadius: 6, padding: '12px 16px' }}>
          <legend style={{ padding: '0 6px', fontSize: 13, fontWeight: 600 }}>E-Rechnung</legend>

          <div className="form-group">
            <label>E-Rechnungs-Format</label>
            <select
              value={formData.e_rechnung_format}
              onChange={(e) => setFormData({ ...formData, e_rechnung_format: e.target.value as ERechnungFormat })}
            >
              <option value="KEINE">Keine (Standard-PDF)</option>
              <option value="ZUGFERD">ZUGFeRD (PDF mit eingebetteter XML)</option>
              <option value="XRECHNUNG">XRechnung (zusätzlich XML als Anhang)</option>
            </select>
          </div>

          {fehlendeERechnungFelder().length > 0 && (
            <div className="error-message">
              Für die E-Rechnung fehlen noch: {fehlendeERechnungFelder().join(', ')}.
            </div>
          )}
          {formData.e_rechnung_format !== 'KEINE' && !formData.ust_idnr.trim() && (
            <div style={{ fontSize: 12, color: 'var(--ink-2)', margin: '4px 0 10px' }}>
              Hinweis: Für Reverse-Charge-Aufträge (§13b UStG) und innergemeinschaftliche
              Lieferungen verlangt die E-Rechnung zusätzlich die USt-IdNr. des Kunden.
            </div>
          )}

          <div className="form-group">
            <label>Straße</label>
            <input
              type="text"
              value={formData.strasse}
              onChange={(e) => setFormData({ ...formData, strasse: e.target.value })}
              placeholder="z.B. Musterweg 1"
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="form-group" style={{ flex: '0 1 120px' }}>
              <label>PLZ</label>
              <input
                type="text"
                value={formData.plz}
                onChange={(e) => setFormData({ ...formData, plz: e.target.value })}
                placeholder="12345"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Ort</label>
              <input
                type="text"
                value={formData.ort}
                onChange={(e) => setFormData({ ...formData, ort: e.target.value })}
                placeholder="Musterstadt"
              />
            </div>
            <div className="form-group" style={{ flex: '0 1 90px' }}>
              <label>Land</label>
              <input
                type="text"
                maxLength={2}
                value={formData.land_code}
                onChange={(e) => setFormData({ ...formData, land_code: e.target.value.toUpperCase() })}
                placeholder="DE"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Leitweg-ID</label>
            <input
              type="text"
              value={formData.leitweg_id}
              onChange={(e) => setFormData({ ...formData, leitweg_id: e.target.value })}
              placeholder="Pflicht bei XRechnung, z.B. 991-12345-67"
            />
          </div>
        </fieldset>

        <CustomFieldsForm
          tabelle="partner"
          authToken={authToken}
          values={customAttributes}
          onChange={setCustomAttributes}
        />

        {editingPartner && (
          <div className="form-group">
            <label>Ansprechpartner</label>
            {apError && <div className="error-message">{apError}</div>}
            {ansprechpartner.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                {ansprechpartner.map(a => (
                  <li key={a.ansprechpartner_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bone-3, #ddd)' }}>
                    <span style={{ flex: 1 }}>
                      <strong>{a.name}</strong>{a.position ? ` – ${a.position}` : ''}
                      {a.ist_hauptkontakt && <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>(Hauptkontakt)</span>}
                      {(a.email || a.telefon) && (
                        <span style={{ display: 'block', fontSize: 12, opacity: 0.7 }}>
                          {[a.email, a.telefon].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => handleDeleteAnsprechpartner(a.ansprechpartner_id)} className="btn-secondary">
                      Entfernen
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Name"
                value={neuerAnsprechpartner.name}
                onChange={(e) => setNeuerAnsprechpartner({ ...neuerAnsprechpartner, name: e.target.value })}
                style={{ flex: '1 1 140px' }}
              />
              <input
                type="text"
                placeholder="Position"
                value={neuerAnsprechpartner.position}
                onChange={(e) => setNeuerAnsprechpartner({ ...neuerAnsprechpartner, position: e.target.value })}
                style={{ flex: '1 1 140px' }}
              />
              <input
                type="email"
                placeholder="E-Mail"
                value={neuerAnsprechpartner.email}
                onChange={(e) => setNeuerAnsprechpartner({ ...neuerAnsprechpartner, email: e.target.value })}
                style={{ flex: '1 1 140px' }}
              />
              <input
                type="tel"
                placeholder="Telefon"
                value={neuerAnsprechpartner.telefon}
                onChange={(e) => setNeuerAnsprechpartner({ ...neuerAnsprechpartner, telefon: e.target.value })}
                style={{ flex: '1 1 140px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={neuerAnsprechpartner.ist_hauptkontakt}
                  onChange={(e) => setNeuerAnsprechpartner({ ...neuerAnsprechpartner, ist_hauptkontakt: e.target.checked })}
                />
                Hauptkontakt
              </label>
              <button type="button" onClick={handleAddAnsprechpartner} className="btn-secondary">
                + Hinzufügen
              </button>
            </div>
          </div>
        )}

        {editingPartner && (formData.typ === 'Kunde' || formData.typ === 'Beides') && (
          <div className="form-group">
            <label>Preiskonditionen (Staffelpreise für diesen Kunden)</label>
            {kError && <div className="error-message">{kError}</div>}
            {konditionen.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                {konditionen.map(k => (
                  <li key={k.kondition_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bone-3, #ddd)' }}>
                    <span style={{ flex: 1 }}>
                      <strong>{artikelName(k.artikel_id)}</strong>
                      {` — ab ${k.staffel_ab_menge} Stück: ${k.preis.toFixed(2)} €`}
                      {(k.gueltig_von || k.gueltig_bis) && (
                        <span style={{ display: 'block', fontSize: 12, opacity: 0.7 }}>
                          gültig {k.gueltig_von || '…'} bis {k.gueltig_bis || '…'}
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => handleDeleteKondition(k.kondition_id)} className="btn-secondary">
                      Entfernen
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={neueKondition.artikel_id}
                onChange={(e) => setNeueKondition({ ...neueKondition, artikel_id: e.target.value })}
                style={{ flex: '1 1 180px' }}
              >
                <option value="">— Artikel wählen —</option>
                {artikelListe.map(a => (
                  <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung}</option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                placeholder="Ab Menge"
                value={neueKondition.staffel_ab_menge}
                onChange={(e) => setNeueKondition({ ...neueKondition, staffel_ab_menge: e.target.value })}
                style={{ flex: '0 1 100px' }}
              />
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Preis/Einheit"
                value={neueKondition.preis}
                onChange={(e) => setNeueKondition({ ...neueKondition, preis: e.target.value })}
                style={{ flex: '0 1 120px' }}
              />
              <button type="button" onClick={handleAddKondition} className="btn-secondary">
                + Hinzufügen
              </button>
            </div>
          </div>
        )}

        {editingPartner && (
          <div className="form-group">
            <AktivitaetenBlock entitaetTyp="partner" entitaetId={editingPartner.partner_id} mitBelegen />
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Abbrechen
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Speichere...' : editingPartner ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;
